import type { RootContent } from "mdast";
import { fromMarkdown } from "mdast-util-from-markdown";
import { Descendant } from "slate";

import { specialChars, isNotNullOrUndefined } from "@smart/itops-utils-basic";

import {
  EditorElement,
  isEditorText,
  Mark,
  ParagraphTag,
  TAB_PLACEHOLDER,
} from "./elements";
import {
  placeholderAstExtension,
  placeholderSyntaxExtension,
} from "./placeholder";

const isOpenUnderlineTag = (node: RootContent) =>
  node.type === "html" &&
  typeof (node as { value?: string }).value === "string" &&
  /^<u\s*\/?>$/i.test((node as { value: string }).value.trim());

const isCloseUnderlineTag = (node: RootContent) =>
  node.type === "html" &&
  typeof (node as { value?: string }).value === "string" &&
  /^<\/u\s*>$/i.test((node as { value: string }).value.trim());

const unescapeHtml = (s: string) =>
  s.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");

type ConvertResult = Descendant | Descendant[] | undefined;

const addMarkToDescendant = (node: Descendant, mark: Mark): Descendant => {
  if (isEditorText(node)) return { ...node, [mark]: true };
  if (
    typeof node === "object" &&
    node !== null &&
    "children" in node &&
    Array.isArray((node as { children: Descendant[] }).children)
  ) {
    return {
      ...node,
      children: (node as { children: Descendant[] }).children.map((child) =>
        addMarkToDescendant(child, mark),
      ),
    };
  }
  return node;
};

const addUnderlineToDescendant = (node: Descendant): Descendant =>
  addMarkToDescendant(node, "underline");

const toDescendantArray = (result: ConvertResult): Descendant[] => {
  if (!result) return [];
  return Array.isArray(result) ? result : [result];
};

declare module "mdast" {
  export interface PhrasingContentMap {
    placeholder: EditorElement<"placeholder">;
  }

  export interface RootContentMap {
    placeholder: EditorElement<"placeholder">;
  }
}

// Tag paragraph children: wrap runs between <u> and </u> with underline
function mergeUnderlineSiblings(
  children: RootContent[],
): Array<{ node: RootContent; underline?: boolean }> {
  const out: Array<{ node: RootContent; underline?: boolean }> = [];
  let i = 0;
  while (i < children.length) {
    const node = children[i];
    if (isOpenUnderlineTag(node)) {
      i += 1;
      while (i < children.length && !isCloseUnderlineTag(children[i])) {
        out.push({ node: children[i], underline: true });
        i += 1;
      }
      if (i < children.length) i += 1;
    } else {
      out.push({ node });
      i += 1;
    }
  }
  return out;
}

const convertInlineChildren = (children: RootContent[]): Descendant[] => {
  const tagged = mergeUnderlineSiblings(children);
  return tagged.flatMap(({ node, underline }) =>
    // eslint-disable-next-line @typescript-eslint/no-use-before-define -- mutual recursion with convertChild
    toDescendantArray(convertChild(node)).map((d) =>
      underline ? addUnderlineToDescendant(d) : d,
    ),
  );
};

const convertMarkedChildren = (
  children: RootContent[],
  mark: Mark,
): ConvertResult => {
  const descendants = convertInlineChildren(children).map((d) =>
    addMarkToDescendant(d, mark),
  );

  if (descendants.length === 0) return { text: "", [mark]: true };
  return descendants.length === 1 ? descendants[0] : descendants;
};

const convertListItem = (
  listItem: RootContent,
): EditorElement<"list-item"> | undefined => {
  if ((listItem as { type: string }).type !== "listItem") return undefined;
  const itemChildren = (listItem as { children: RootContent[] }).children;
  const firstParagraph = itemChildren.find(
    (c: RootContent) => c.type === "paragraph",
  );
  if (!firstParagraph) {
    return { type: "list-item", children: [{ text: "" }] };
  }
  const childrenDescendants = convertInlineChildren(
    (firstParagraph as { children: RootContent[] }).children,
  );
  return {
    type: "list-item",
    children:
      childrenDescendants.length > 0 ? childrenDescendants : [{ text: "" }],
  };
};

const convertLinkChild = (child: RootContent): EditorElement<"link"> => {
  const childrenDescendants = convertInlineChildren(
    (child as { children: RootContent[] }).children,
  );
  return {
    type: "link",
    url: (child as { url: string }).url,
    children:
      childrenDescendants.length > 0 ? childrenDescendants : [{ text: "" }],
  };
};

const convertHeadingChild = (
  child: RootContent,
): EditorElement<"paragraph"> => {
  const depth = (child as { depth?: number }).depth ?? 1;
  const tag = `h${Math.min(6, Math.max(1, depth))}` as ParagraphTag;
  const headingChildren = (child as { children: RootContent[] }).children;
  const childrenDescendants = convertInlineChildren(headingChildren);
  return {
    type: "paragraph",
    paragraphTag: tag,
    children:
      childrenDescendants.length > 0 ? childrenDescendants : [{ text: "" }],
  };
};

const convertParagraphChild = (
  child: RootContent,
): EditorElement<"paragraph"> => {
  const { children } = child as { children: RootContent[] };
  if (
    children.length === 1 &&
    children[0].type === "text" &&
    (children[0] as { value: string }).value === specialChars.nbsp
  ) {
    return { type: "paragraph", children: [{ text: "" }] };
  }

  if (children.length === 1 && children[0].type === "placeholder") {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const converted = toDescendantArray(convertChild(children[0]));
    if (converted.length)
      return {
        type: "paragraph",
        children: [{ text: "" }, ...converted, { text: "" }],
      };
  }

  const childrenDescendants = convertInlineChildren(children);
  return {
    type: "paragraph",
    children:
      childrenDescendants.length > 0 ? childrenDescendants : [{ text: "" }],
  };
};

const convertTextChild = (child: RootContent): Descendant | undefined => {
  let text = (child as { value: string }).value.replace(
    new RegExp(specialChars.invisible, "g"),
    "",
  );
  text = text.replace(new RegExp(TAB_PLACEHOLDER, "g"), "\t");
  if (text.length === 0) return undefined;
  return { text };
};

const convertHtmlChild = (child: RootContent): Descendant | undefined => {
  const value = (child as { value?: string }).value ?? "";
  const match = value.match(/^<u[^>]*>([\s\S]*?)<\/u\s*>$/i);
  if (match) {
    return { text: unescapeHtml(match[1]), underline: true };
  }
  return { text: "" };
};

function convertChild(child: RootContent): ConvertResult {
  switch (child.type) {
    case "list": {
      const listNode = child as { ordered?: boolean; children: RootContent[] };
      const listItems = listNode.children;
      const itemNodes = listItems
        .map(convertListItem)
        .filter(isNotNullOrUndefined) as EditorElement<"list-item">[];
      if (listNode.ordered) {
        return { type: "numbered-list", children: itemNodes };
      }
      return { type: "bulleted-list", children: itemNodes };
    }
    case "blockquote": {
      const quoteChildren = (
        child as { children: RootContent[] }
      ).children.flatMap((c) => toDescendantArray(convertChild(c)));
      if (!quoteChildren.length) return undefined;
      return {
        type: "blockquote",
        children: quoteChildren,
      } as EditorElement<"blockquote">;
    }
    case "link":
      return convertLinkChild(child);
    case "placeholder":
      return {
        type: "placeholder",
        displayText: child.displayText,
        name: child.name,
        isLink: child.isLink,
        casing: child.casing,
        children: [{ text: "" }],
      };
    case "heading":
      return convertHeadingChild(child);
    case "paragraph":
      return convertParagraphChild(child);
    case "text":
      return convertTextChild(child);
    case "strong":
      return convertMarkedChildren(child.children, "bold");
    case "emphasis":
      return convertMarkedChildren(child.children, "italic");
    case "html":
      return convertHtmlChild(child);
    default:
      return { text: "" };
  }
  return { text: "" };
}

const preserveEmptyLines = (md: string): string =>
  md.replace(/(\n\n)(\n\n)+/g, (match) => {
    const pairCount = match.length / 2;
    return `\n\n${`${specialChars.nbsp}\n\n`.repeat(pairCount - 1)}`;
  });

export const deserialize = (md?: string): Descendant[] => {
  const normalized = md ? preserveEmptyLines(md) : "";
  const parsed = normalized
    ? fromMarkdown(normalized, {
        extensions: [placeholderSyntaxExtension],
        mdastExtensions: [placeholderAstExtension],
      }).children
    : [];

  const defaultNode: Descendant[] = [
    { type: "paragraph", children: [{ text: "" }] },
  ];
  if (!parsed.length) return defaultNode;

  const result = parsed.flatMap((child) =>
    toDescendantArray(convertChild(child)),
  );
  return result.length ? result : defaultNode;
};
